import { useEffect, useState } from "react";
import { Card, Title } from "@tremor/react";
import {
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Input,
  Modal,
  Popconfirm,
  Table,
  Tag,
  Typography,
} from "antd";

import {
  COLLECTIONS_AUTOMATION_SETTING_LABELS,
  DEFAULT_COLLECTIONS_AUTOMATION_SETTINGS,
  describeCollectionsAutomation,
  isCollectionsAutomationOn,
  type CollectionsAutomationSettings,
  type CollectionsSignature,
} from "@fitness-app/data-models/entities/CollectionsCase";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";

import { holdLabel, stepLabel, taskLabel } from "../collectionsLabels";
import {
  useRunCollectionsAutomation,
  useUpdateCollectionsSettings,
  useUpdateCollectionsSignature,
  type AutomationRunSummary,
  type PlannedAutomationAction,
} from "../hooks/useCollections";

const CHANNEL_LABELS: Record<string, string> = { email: "mail", sms: "SMS", letter: "pismo PDF" };
const SETTING_KEYS = Object.keys(COLLECTIONS_AUTOMATION_SETTING_LABELS) as (keyof CollectionsAutomationSettings)[];

const SIGNATURE_FIELDS: { key: keyof CollectionsSignature; label: string; placeholder: string }[] = [
  { key: "name", label: "Imię", placeholder: "Anita" },
  { key: "title", label: "Linia pod imieniem", placeholder: "Dział rozliczeń, Program Hollywood" },
  { key: "email", label: "E-mail do odpowiedzi", placeholder: "kontakt@programhollywood.pl" },
  { key: "phone", label: "Telefon (opcjonalnie)", placeholder: "+48 600 100 200" },
];

const EMPTY_SIGNATURE: CollectionsSignature = { name: "", title: "", email: "", phone: null };

const sameSignature = (a: CollectionsSignature, b: CollectionsSignature) =>
  SIGNATURE_FIELDS.every(({ key }) => (a[key] ?? "") === (b[key] ?? ""));

const signatureValid = (signature: CollectionsSignature) =>
  Boolean(signature.name.trim() && signature.title.trim() && /^\S+@\S+\.\S+$/.test(signature.email.trim()));

const columns = [
  {
    title: "Klientka",
    dataIndex: "clientEmail",
    key: "client",
    render: (email: string, row: PlannedAutomationAction) =>
      row.caseId ? (
        <a href={`/products/collections/${row.caseId}`} target="_blank" rel="noreferrer">
          {email}
        </a>
      ) : (
        email
      ),
  },
  {
    title: "Wiadomość",
    key: "step",
    render: (_: unknown, row: PlannedAutomationAction) =>
      row.step ? (
        <span>
          {stepLabel(row.step)}
          {row.channels.length ? (
            <span className="text-gray-500">
              {" "}
              · {row.channels.map((channel) => CHANNEL_LABELS[channel] ?? channel).join(" + ")}
            </span>
          ) : null}
        </span>
      ) : (
        <span className="text-gray-500">{row.note ?? holdLabel(row.hold)}</span>
      ),
  },
  {
    title: "Zadania",
    key: "tasks",
    render: (_: unknown, row: PlannedAutomationAction) =>
      row.tasks.length ? row.tasks.map(taskLabel).join(", ") : "—",
  },
  {
    title: "Nowy status sprawy",
    key: "status",
    render: (_: unknown, row: PlannedAutomationAction) => (row.status ? <Tag>{row.status}</Tag> : "—"),
  },
];

interface AutomationPanelProps {
  /** Undefined while the dashboard loads. */
  settings: CollectionsAutomationSettings | undefined;
  /** Who signs the mails, SMS and letters; undefined while the dashboard loads. */
  signature: CollectionsSignature | null | undefined;
}

const sameSettings = (a: CollectionsAutomationSettings, b: CollectionsAutomationSettings) =>
  SETTING_KEYS.every((key) => a[key] === b[key]);

/**
 * The owner's switches for the automatic messages (TRE-2169) plus a dry run
 * and the real run for today. Everyone with the tab sees what is on; only the
 * owner can change it — the backend enforces the same.
 */
const AutomationPanel = ({ settings, signature }: AutomationPanelProps) => {
  const { isOwner } = useUserClaims();
  const run = useRunCollectionsAutomation();
  const update = useUpdateCollectionsSettings();
  const [preview, setPreview] = useState<AutomationRunSummary | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const saved = settings ?? DEFAULT_COLLECTIONS_AUTOMATION_SETTINGS;
  const [draft, setDraft] = useState<CollectionsAutomationSettings>(saved);

  useEffect(() => {
    setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved.info, saved.reminders, saved.demands, saved.sms]);

  const updateSignature = useUpdateCollectionsSignature();
  const savedSignature = signature ?? EMPTY_SIGNATURE;
  const [signatureDraft, setSignatureDraft] = useState<CollectionsSignature>(savedSignature);

  useEffect(() => {
    setSignatureDraft(savedSignature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSignature.name, savedSignature.title, savedSignature.email, savedSignature.phone]);

  const dirty = !sameSettings(draft, saved);
  const signatureDirty = !sameSignature(signatureDraft, savedSignature);
  const closeSettings = () => {
    setSettingsOpen(false);
    setDraft(saved);
    setSignatureDraft(savedSignature);
  };
  // One „Zapisz" for both parts; each request goes only when its part changed.
  const saveSettings = async () => {
    try {
      if (dirty) {
        await update.mutateAsync(draft);
      }
      if (signatureDirty) {
        await updateSignature.mutateAsync({ ...signatureDraft, phone: signatureDraft.phone?.trim() || null });
      }
      setSettingsOpen(false);
    } catch {
      // The hooks already told the user what failed; the drawer stays open with the draft.
    }
  };
  const on = isCollectionsAutomationOn(saved);
  const sentTotal = (summary: AutomationRunSummary) =>
    Object.values(summary.sent).reduce((sum, count) => sum + (count ?? 0), 0);

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Title>Automatyczne wiadomości do dłużniczek</Title>
          <Typography.Text type="secondary">
            {on ? `Włączone: ${describeCollectionsAutomation(saved)}` : "Wyłączone - nic nie wychodzi"}
            {" · "}wysyłka codziennie o 9:05 oprócz niedziel
          </Typography.Text>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setSettingsOpen(true)} disabled={settings === undefined}>
            {isOwner ? "Ustawienia" : "Co jest włączone"}
          </Button>
          <Button
            onClick={() => run.mutate({ dryRun: true }, { onSuccess: (summary) => setPreview(summary) })}
            loading={run.isLoading && run.variables?.dryRun === true}
            disabled={run.isLoading && run.variables?.dryRun === false}
          >
            Pokaż, co dziś wyjdzie
          </Button>
          <Popconfirm
            title="Wysłać dzisiejsze wiadomości teraz?"
            description="Pójdą maile i SMS-y zaplanowane na dziś i powstaną zadania, tak samo jak przy porannej wysyłce o 9:05."
            okText="Wyślij"
            cancelText="Anuluj"
            disabled={!on}
            onConfirm={() => run.mutate({ dryRun: false })}
          >
            <Button
              type="primary"
              disabled={!on || (run.isLoading && run.variables?.dryRun === true)}
              loading={run.isLoading && run.variables?.dryRun === false}
            >
              Wyślij dzisiejsze
            </Button>
          </Popconfirm>
        </div>
      </div>

      <Drawer
        title="Automatyczne wiadomości"
        open={settingsOpen}
        onClose={closeSettings}
        width={520}
        footer={
          isOwner ? (
            <div className="flex justify-end gap-2">
              <Button onClick={closeSettings}>Anuluj</Button>
              <Button
                type="primary"
                disabled={!(dirty || signatureDirty) || (signatureDirty && !signatureValid(signatureDraft))}
                loading={update.isLoading || updateSignature.isLoading}
                onClick={() => void saveSettings()}
              >
                Zapisz
              </Button>
            </div>
          ) : null
        }
      >
        <div className="flex flex-col gap-4">
          {SETTING_KEYS.map((key) => (
            <Checkbox
              key={key}
              checked={draft[key]}
              disabled={!isOwner || settings === undefined}
              onChange={(event) => setDraft((prev) => ({ ...prev, [key]: event.target.checked }))}
              className="items-start"
            >
              <span className="font-medium">{COLLECTIONS_AUTOMATION_SETTING_LABELS[key].label}</span>
              <span className="block text-xs text-gray-500">
                {COLLECTIONS_AUTOMATION_SETTING_LABELS[key].description}
              </span>
            </Checkbox>
          ))}
          <Divider className="my-2" />
          <Typography.Text strong>Podpis wiadomości</Typography.Text>
          {SIGNATURE_FIELDS.map(({ key, label, placeholder }) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-sm">{label}</span>
              <Input
                value={signatureDraft[key] ?? ""}
                placeholder={placeholder}
                disabled={!isOwner || signature === undefined}
                status={
                  key !== "phone" && signatureDirty && !signatureValid(signatureDraft) && !signatureDraft[key]?.trim()
                    ? "error"
                    : undefined
                }
                onChange={(event) => setSignatureDraft((prev) => ({ ...prev, [key]: event.target.value }))}
              />
            </label>
          ))}
          {isOwner ? null : (
            <Typography.Text type="secondary" className="text-xs">
              Zmienić może tylko właściciel konta.
            </Typography.Text>
          )}
        </div>
      </Drawer>

      <Modal
        open={preview !== null}
        onCancel={() => setPreview(null)}
        footer={null}
        width={960}
        title={preview ? `Co wyszłoby dziś (${preview.today}) — podgląd, nic nie zostało wysłane` : ""}
      >
        {preview ? (
          <>
            <Descriptions size="small" column={3} className="mb-4">
              <Descriptions.Item label="Włączone">{describeCollectionsAutomation(preview.settings)}</Descriptions.Item>
              <Descriptions.Item label="Sprawy">{preview.cases}</Descriptions.Item>
              <Descriptions.Item label="Wiadomości">{sentTotal(preview)}</Descriptions.Item>
              <Descriptions.Item label="Zadania">{preview.tasks + preview.bookkeeping.reviews}</Descriptions.Item>
              <Descriptions.Item label="Deklaracje">
                {preview.bookkeeping.declarationsKept} dotrzymane / {preview.bookkeeping.declarationsBroken} złamane
              </Descriptions.Item>
              <Descriptions.Item label="Wstrzymane">
                {Object.entries(preview.holds)
                  .map(([hold, count]) => `${count} × ${holdLabel(hold)}`)
                  .join(", ") || "—"}
              </Descriptions.Item>
            </Descriptions>
            <Table
              size="small"
              rowKey={(row) => `${row.caseId ?? row.clientEmail}:${row.step ?? row.note ?? row.hold}`}
              columns={columns}
              dataSource={preview.actions}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              locale={{ emptyText: "Dziś nic nie wychodzi" }}
            />
          </>
        ) : null}
      </Modal>
    </Card>
  );
};

export default AutomationPanel;
